import {
  Acoes,
  Campo,
  CampoHeader,
  Coluna,
  CopiarPadraoButton,
  EditorPreviewRow,
  Input,
  Placeholders,
  PreviewFrame,
  SalvarButton,
  Textarea,
  Wrapper,
} from './style'
import { CampoTemplate, useEditorTemplate } from './useEditorTemplate'

interface EditorTemplateProps {
  titulo: string
  campos: CampoTemplate[]
}

// Renderiza ${nome} sem disparar a regra no-template-curly-in-string
// (o literal '${' não fecha chave, então não é tratado como template).
function Variavel({ nome }: { nome: string }) {
  return (
    <code>
      {'${'}
      {nome}
      {'}'}
    </code>
  )
}

// Campos disponíveis em cada item de `produtos` (PredicaoPedidoProdutoEnvioEmailDTO).
const PRODUTO_PLACEHOLDERS = [
  'produtos[i].nome',
  'produtos[i].codigoIntegracao',
  'produtos[i].quantidade',
  'produtos[i].id',
]

function AjudaPlaceholders({
  placeholders,
  produtoPlaceholders,
  nota,
}: {
  placeholders: string[]
  produtoPlaceholders?: string[]
  nota?: string
}) {
  const porItem = produtoPlaceholders ?? PRODUTO_PLACEHOLDERS
  return (
    <Placeholders>
      <b>Variáveis disponíveis:</b>{' '}
      {placeholders.map(nome => (
        <Variavel key={nome} nome={nome} />
      ))}
      <div style={{ marginTop: 6 }}>
        <b>Por produto</b> (dentro do laço):{' '}
        {porItem.map(nome => (
          <Variavel key={nome} nome={nome} />
        ))}
      </div>
      <div style={{ marginTop: 6 }}>
        Laço para percorrer os produtos:{' '}
        <code>{'<% for(var i = 0; i < produtos.size(); i++) { %>'}</code>{' '}
        <Variavel nome="produtos[i].nome" /> <code>{'<% } %>'}</code>
      </div>
      {nota ? (
        <div style={{ marginTop: 6 }}>{nota}</div>
      ) : (
        <div style={{ marginTop: 6 }}>
          Quantidade formatada:{' '}
          <code>
            {
              '<%= br.com.w3erp.commons.util.Util.descriptionDecimal(produtos[i].quantidade) %>'
            }
          </code>
        </div>
      )}
    </Placeholders>
  )
}

export function EditorTemplate({ titulo, campos }: EditorTemplateProps) {
  const { valores, carregando, salvando, handleChange, copiarPadrao, salvar } =
    useEditorTemplate(campos)

  function renderEditor(campo: CampoTemplate, valor: string) {
    if (campo.tipo === 'input') {
      return (
        <Input
          id={campo.nome}
          value={valor}
          onChange={e => handleChange(campo.nome, e.target.value)}
        />
      )
    }
    if (campo.preview) {
      return (
        <EditorPreviewRow>
          <Coluna>
            <span className="coluna-titulo">Código (HTML)</span>
            <Textarea
              id={campo.nome}
              rows={campo.rows ?? 18}
              value={valor}
              onChange={e => handleChange(campo.nome, e.target.value)}
            />
          </Coluna>
          <Coluna>
            <span className="coluna-titulo">
              Pré-visualização (variáveis aparecem literais)
            </span>
            <PreviewFrame
              title={`preview-${campo.nome}`}
              sandbox=""
              srcDoc={valor}
            />
          </Coluna>
        </EditorPreviewRow>
      )
    }
    return (
      <Textarea
        id={campo.nome}
        rows={campo.rows ?? 10}
        value={valor}
        onChange={e => handleChange(campo.nome, e.target.value)}
      />
    )
  }

  if (carregando) {
    return (
      <Wrapper>
        <h2>{titulo}</h2>
        <p>Carregando...</p>
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <h2>{titulo}</h2>

      {campos.map(campo => {
        const valor = valores[campo.nome] ?? ''
        return (
          <Campo key={campo.nome}>
            <CampoHeader>
              <label htmlFor={campo.nome}>{campo.label}</label>
              <CopiarPadraoButton
                type="button"
                onClick={() => copiarPadrao(campo.nome)}
              >
                Copiar padrão do sistema
              </CopiarPadraoButton>
            </CampoHeader>

            {renderEditor(campo, valor)}

            {campo.placeholders && campo.placeholders.length > 0 && (
              <AjudaPlaceholders
                placeholders={campo.placeholders}
                produtoPlaceholders={campo.produtoPlaceholders}
                nota={campo.nota}
              />
            )}
          </Campo>
        )
      })}

      <Acoes>
        <SalvarButton type="button" onClick={salvar} disabled={salvando}>
          {salvando ? 'Salvando...' : 'Salvar'}
        </SalvarButton>
      </Acoes>
    </Wrapper>
  )
}
